import { Controller } from '@hotwired/stimulus';

import { fetchProperties } from '../../../api/properties'

import SET_MAP from '../../../utils/mapbox/config/setMap'
import SET_MARKERS from '../../../utils/mapbox/config/setMarkers'

import { generateLayerId, SOURCE_ID } from '../../../utils/mapbox/config/options'

const COLORS = { 'blue-100': '#5076F6', 'purple-100': '#21175A', 'neutral-30': '#4B485F', 'neutral-40': '#BCBAC3', transparent: 'transparent' };

export default class extends Controller {
  static values = {
    publicKey: String,
    style: String,
    propertyLat: String,
    propertyLng: String,
    quarterLat: String,
    quarterLng: String,
    hosmanSectorId: Number,
    zoom: Number
  }

  static targets = [
    'generalContent',
    'customContent',
    'soldPropertiesCount',
    'soldPropertiesCountTitle',
    'awaitingOfferPropertiesCount',
    'awaitingOfferPropertiesCard',
    'soldPropertiesText',
    'awaitingOfferPropertiesText'
  ]

  connect = async () => {
    await this.handleProperties()
    if (this.properties.length > 3) this.customContentTargets.forEach(content => content.classList.remove('hidden'))
    else this.generalContentTarget.classList.remove('hidden')
    this.mapboxMap = SET_MAP.initMap(this.publicKeyValue, [this.quarterLngValue, this.quarterLatValue], this.zoomValue ? this.zoomValue : 13, this.styleValue, 'sales-sector-map')
    this.mapboxMapLoaded()
    await this.handleMarkers()
    await this.handleCardContent()
    if (window.matchMedia("(max-width: 430px)").matches) this.handleMapWithSmallScreen()
  }

  mapboxMapLoaded = () => {
    this.mapboxMap.on('load', async () => {
      try {
        const response = await fetch(`/api/v1/quarters/${this.hosmanSectorIdValue}/geojson`);
        if (!response.ok) return;

        const quarterGeojson = await response.json();
        this.mapboxMap.addSource(SOURCE_ID, { type: 'geojson', data: quarterGeojson });

        this.mapboxMap.addLayer({
          id: generateLayerId('fill', 'quarter'),
          type: 'fill',
          source: SOURCE_ID,
          paint: {
            'fill-color': COLORS['blue-100'],
            'fill-opacity': 0.3
          }
        });

        this.mapboxMap.addLayer({
          id: generateLayerId('line', 'quarter'),
          type: 'line',
          source: SOURCE_ID,
          paint: {
            'line-color': COLORS['blue-100'],
            'line-width': 3
          }
        });
      } catch (error) {
        // Do nothing: map remains visible with markers even if polygon cannot be loaded.
      }
    })
  }

  handleProperties = async () => {
    this.properties = await fetchProperties(this.hosmanSectorIdValue);
    this.propertiesCount = this.properties.length
    this.propertiesSoldCount = this.properties.filter(property => property.state === 'sold').length
    this.propertiesAwaitingOfferCount = this.propertiesCount - this.propertiesSoldCount
  }

  handleMarkers = () => {
    // handle main marker
    SET_MARKERS.main(this.mapboxMap, [this.propertyLngValue, this.propertyLatValue])
    // handle others marker
    const soldPropertiesCount = this.properties.filter(property => property.state === 'sold').length
    if (soldPropertiesCount > 0) this.properties.forEach(property => SET_MARKERS.specific(this.mapboxMap, property))
  }

  handleCardContent = () => {
    if (this.hasSoldPropertiesCountTarget) this.soldPropertiesCountTarget.innerHTML = `${this.propertiesSoldCount < 10 ? 0 : ''}${this.propertiesSoldCount}`
    if (this.hasSoldPropertiesCountTitleTarget) this.soldPropertiesCountTitleTarget.innerHTML = this.propertiesSoldCount
    if (this.propertiesAwaitingOfferCount === 0) this.awaitingOfferPropertiesCardTarget.classList.add('hidden')
    if (this.hasAwaitingOfferPropertiesCountTarget) this.awaitingOfferPropertiesCountTarget.innerHTML = `${this.propertiesAwaitingOfferCount < 10 ? 0 : ''}${this.propertiesAwaitingOfferCount}`
    if (this.hasSoldPropertiesTextTarget) this.soldPropertiesTextTarget.innerHTML = `Bien${this.propertiesSoldCount > 1 ? 's' : ''} vendu${this.propertiesSoldCount > 1 ? 's' : ''}`
    if (this.hasAwaitingOfferPropertiesTextTarget) this.awaitingOfferPropertiesTextTarget.innerHTML = `Bien${this.propertiesAwaitingOfferCount > 1 ? 's' : ''} à vendre`
  }

  handleMapWithSmallScreen = () => {
    this.mapboxMap.scrollZoom.disable();
    this.mapboxMap.dragPan.disable();
  }
}
